import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pushModal } from '../../Modals/redux/basicActions';
import { ModalType } from '../../Modals/redux/types';
import { getCurrentUser } from '../../publicSession/selectors';
import { getCurrentItem } from '../../Duperbrowse/redux/selectors';
import {
  BiContextProvidedValue,
  BiData,
  ExternalLinkClick,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '../../types/resource';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { makeXHRCall } from '../../simpleXHR/actionCreators';
import { SimpleXHREndpoint, SimpleXHRMethod } from '../../simpleXHR/constants';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { ScamLevelType } from '@wix/da-bi/pkg/constants';

const DA_DOMAINS: DaUrlEntryType[] = [
  { domain: 'deviantart', tld: ['com', 'net'] },
  { domain: 'sta', tld: ['sh'] },
  { domain: 'dreamup', tld: ['com'] },
  { domain: 'deviantartsell', tld: ['com'] },
  { domain: 'deviantartsupport', tld: ['com'] },
  { domain: 'fav', tld: ['me'] },
];

export function useSafeUrlHandler() {
  const hasScamWarningFeature = useFlag('scam_warning');
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);
  const currentDeviation = useSelector(getCurrentItem);
  const logBiEvent = useBiLogger();

  // Modals are going to be outside of the BiLoggerContextProvider, so we need to pass those values
  // along in the pushModal call
  const { sectionname, typeid, message_id } = useContext(BiContext);

  const safeUrlHandler = useCallback(
    async e => {
      if (currentUser.userId === currentDeviation?.author?.userId) {
        return;
      }
      const clickedEl = e.target;

      // Closest is inclusive of the target, and should be safe as
      // we cant have anchor inside anchor.
      const anchor = (clickedEl as HTMLElement).closest('a');
      if (anchor) {
        let href = anchor.getAttribute('href') ?? '';
        href = href.replace(
          /^https?:\/\/www\.deviantart\..*\/users\/outgoing\?/i,
          ''
        );
        if (href.length < 1) {
          return;
        }
        let isDaDomain = false;
        try {
          const url = new URL(href);
          const isHttpOrHttps =
            url.protocol === 'http:' || url.protocol === 'https:';
          const hostname = url.hostname.toLowerCase();
          if (hostname === '#') {
            isDaDomain = true;
          }
          const subdomains = hostname.split('.');
          if (!isDaDomain && subdomains.length >= 2) {
            const domain = subdomains[subdomains.length - 2];
            const tld = subdomains[subdomains.length - 1];
            isDaDomain = DA_DOMAINS.some(
              d => d.domain === domain && d.tld.includes(tld)
            );
          }

          if (isDaDomain) {
            logBiEvent(
              BiData<ExternalLinkClick>({
                evid: 364,
                link_url: href,
                sectionname: BiContextProvidedValue,
                itemid: currentDeviation?.deviationId ?? 0,
                typeid: BiContextProvidedValue,
                message_id: BiContextProvidedValue,
                scam_level: ScamLevelType.SCAM_LEVEL_NATIVE,
              })
            );
          }

          if (!(isHttpOrHttps && isDaDomain)) {
            e.preventDefault();
            e.stopPropagation();

            if (hasScamWarningFeature) {
              dispatch(
                makeXHRCall({
                  papiBase: 'dashared',
                  endpoint: SimpleXHREndpoint.SAFE_URL_CHECK,
                  method: SimpleXHRMethod.POST,
                  data: {
                    url: href,
                  } as UrlCheckRequest,
                  onSuccess: (response: UrlCheckResponse) => {
                    switch (response.status) {
                      case 'ok':
                        logBiEvent(
                          BiData<ExternalLinkClick>({
                            evid: 364,
                            link_url: href,
                            sectionname: BiContextProvidedValue,
                            itemid: currentDeviation?.deviationId ?? 0,
                            typeid: BiContextProvidedValue,
                            message_id: BiContextProvidedValue,
                            scam_level: ScamLevelType.SCAM_LEVEL_SAFE,
                          })
                        );
                        dispatch(
                          pushModal(ModalType.SAFE_URL, {
                            params: { href, sectionname, typeid, message_id },
                            wrapInModal: false,
                          })
                        );
                        break;
                      case 'warn':
                        logBiEvent(
                          BiData<ExternalLinkClick>({
                            evid: 364,
                            link_url: href,
                            sectionname: BiContextProvidedValue,
                            itemid: currentDeviation?.deviationId ?? 0,
                            typeid: BiContextProvidedValue,
                            message_id: BiContextProvidedValue,
                            scam_level: ScamLevelType.SCAM_LEVEL_WARN,
                          })
                        );
                        dispatch(
                          pushModal(ModalType.SCAM_WARNING, {
                            params: { href, sectionname, typeid, message_id },
                            wrapInModal: false,
                          })
                        );
                        break;
                      case 'deny':
                      default:
                        logBiEvent(
                          BiData<ExternalLinkClick>({
                            evid: 364,
                            link_url: href,
                            sectionname: BiContextProvidedValue,
                            itemid: currentDeviation?.deviationId ?? 0,
                            typeid: BiContextProvidedValue,
                            message_id: BiContextProvidedValue,
                            scam_level: ScamLevelType.SCAM_LEVEL_DENY,
                          })
                        );
                        // For now, they just want us to go to / on scams that are blocked
                        window.open('https://deviantart.com/', '_blank');
                        break;
                    }
                  },
                  onError: (response: any) => {
                    // On errors, just let them go to the url
                    window.open(href, '_blank', 'noopener, noreferrer');
                  },
                })
              );
            } else {
              logBiEvent(
                BiData<ExternalLinkClick>({
                  evid: 364,
                  link_url: href,
                  sectionname: BiContextProvidedValue,
                  itemid: currentDeviation?.deviationId ?? 0,
                  typeid: ResourceType.DEVIATION,
                })
              );
              dispatch(
                pushModal(ModalType.SAFE_URL, {
                  params: { href, sectionname, typeid, message_id },
                  wrapInModal: false,
                })
              );
            }
          }
        } catch {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    },
    [
      currentDeviation?.author?.userId,
      currentDeviation?.deviationId,
      currentUser.userId,
      dispatch,
      hasScamWarningFeature,
      logBiEvent,
      message_id,
      sectionname,
      typeid,
    ]
  );

  return safeUrlHandler;
}

type DaUrlEntryType = {
  domain: string;
  tld: string[];
};

type UrlResponseType = 'ok' | 'warn' | 'deny';

interface UrlCheckRequest {
  url: string;
}
interface UrlCheckResponse {
  status: UrlResponseType;
}
